import type { IManagementReportDataset } from '@commenda/api';
import { EmptyState } from '@commenda/kakashi';
import { useMemo, useRef } from 'react';

import { ReportCanvas } from './components/ReportCanvas';
import { ReportProvider } from './components/ReportProvider';
import { ReportToolbar } from './components/ReportToolbar';
import { MonthKeyHelper } from './helpers/MonthKeyHelper';

export interface IManagementReportProps {
  dataset: IManagementReportDataset;
  /** ISO 4217 currency the statements are reported in. */
  currency: string;
  /** Column headers, breakdown lists and clean chart defaults (MANAGEMENT_REPORT_COLUMN_LAYOUT). */
  columnLayout?: boolean;
  /** Period override via card menu submenus instead of the dialog (MANAGEMENT_REPORT_INLINE_OVERRIDE). */
  inlineOverride?: boolean;
  /**
   * Report every synced month instead of only months with completed bookkeeping
   * (MANAGEMENT_REPORT_SYNCED_STATEMENTS_GATE).
   */
  syncedStatementsGate?: boolean;
}

const ReportSurface = () => {
  const canvasRef = useRef<HTMLDivElement>(null);

  return (
    <div className="flex flex-col gap-3xl">
      <ReportToolbar canvasRef={canvasRef} />
      <div className="min-h-[480px]">
        <ReportCanvas ref={canvasRef} />
      </div>
    </div>
  );
};

/** Management Reporting tab: MIS built from RootFi statements. */
export const ManagementReport = ({
  dataset,
  currency,
  columnLayout = false,
  inlineOverride = false,
  syncedStatementsGate = false,
}: IManagementReportProps) => {
  const reportDataset = useMemo(
    () =>
      syncedStatementsGate
        ? {
            ...dataset,
            booksClosedThrough: MonthKeyHelper.latest(
              dataset.months.map((m) => m.month)
            ),
          }
        : dataset,
    [dataset, syncedStatementsGate]
  );
  const { booksClosedThrough } = reportDataset;
  const hasReportableMonths =
    booksClosedThrough !== null &&
    reportDataset.months.some((m) => m.month <= booksClosedThrough);

  if (!hasReportableMonths) {
    return syncedStatementsGate ? (
      <EmptyState
        heading="No financial statements synced yet"
        description="Management reports are built from the profit & loss, balance sheet and cash flow statements synced from your accounting software. Figures will appear once they sync."
      />
    ) : (
      <EmptyState
        heading="No closed books yet"
        description="Management reports only include months whose bookkeeping has been completed. Figures will appear once the first period is closed."
      />
    );
  }

  return (
    <ReportProvider
      dataset={reportDataset}
      currency={currency}
      booksClosedThrough={booksClosedThrough}
      columnLayout={columnLayout}
      inlineOverride={inlineOverride}
    >
      <ReportSurface />
    </ReportProvider>
  );
};
